/*
 * Copyright (c) 2010 Macrofocus GmbH. All Rights Reserved.
 */
package org.mkui.palette

import com.macrofocus.common.collection.SortedSet
import com.macrofocus.common.collection.TreeSet
import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.SimpleProperty
import org.mkui.color.*
import com.macrofocus.common.annotation.JsExportClass
import kotlin.js.JsName

/**
 * Palette where colors are interpolated according to some fixed points.
 */
@JsExportClass
class InterpolatedPalette constructor(var entries: SortedSet<Entry>) : FixedPalette(false, emptyList()) {
    private enum class Mode {
        Ramps, Bands
    }

    private val mode: MutableProperty<Mode> = SimpleProperty(Mode.Ramps)

    @JsName("paletteConstructor")
    constructor(p: Palette) : this(TreeSet<Entry>(compareBy<Entry> { it.fraction })) {
        for (i in 0 until p.colorCount) {
            val c: MkColor = p.getColorAt(i)
            entries.add(Entry(i.toDouble() / (p.colorCount - 1), c))
        }
        updateFixedColorGradient()
    }

    @JsName("entriesConstructor")
    constructor(vararg e: Entry) : this(TreeSet<Entry>(compareBy<Entry> { it.fraction }, e.asList()))
     {
        updateFixedColorGradient()
    }

    private fun updateFixedColorGradient() {
        if (!entries.isEmpty()) {
            val colors: MutableList<MkColor> = ArrayList()
            for (i in 0..255) {
                val fraction = 1.0 * i / 255
                val color: MkColor? = computeColor(fraction)
                if (color != null) {
                    colors.add(color)
                } else {
                    throw IllegalStateException("Null color " + fraction + ", " + color)
                }
            }
            this.colors = colors
            this.colorCount = colors.size
        } else {
            this.colors = emptyList()
            this.colorCount = 0
        }
    }
    //    public CPColor getColor(double fraction) {
    //        boolean inverted = isInverted();
    //
    //        switch (getCycle()) {
    //            case REFLECT:
    //                inverted = ((int) fraction) % 2 == 0;
    //            case REPEAT:
    //                fraction = fraction - (int) fraction;
    //        }
    //
    //        if (fraction < getLowestFraction()) {
    //            return getColor(getLowestFraction());
    //        } else if (fraction > getHighestFraction()) {
    //            return getColor(getHighestFraction());
    //        } else {
    //            final CPColor color = computeColor(!inverted ? fraction : 1 - fraction);
    //            return color;
    //        }
    //    }
    /**
     * Returns the Color at the specified position.
     *
     * @param fraction the normalized value
     * @return the color
     */
    private fun computeColor(fraction: Double): MkColor? {
        if (!entries.isEmpty()) {
            var previous: Entry? = null
            for (current in entries) {
                if (previous != null) {
                    if (previous.fraction <= fraction && fraction <= current.fraction) {
                        val v = (fraction - previous.fraction) / (current.fraction - previous.fraction)
                        return interpolate(previous.getColor(), current.getColor(), v)
                    }
                }
                previous = current
            }
            if (fraction < entries.first().fraction) {
                return entries.first().getColor()
            }
            if (fraction > entries.last().fraction) {
                return entries.last().getColor()
            }
        }
        return null
    }

    private fun interpolate(c1: MkColor, c2: MkColor, p: Double): MkColor {
        return when (mode.value) {
            Mode.Ramps -> {
                if (p == 0.0) return c1
                if (p == 1.0) c2 else colorOf(
                    (c1.getURed().toDouble() * (1.0 - p) + c2.getURed().toDouble() * p).toInt(),
                    (c1.getUGreen().toDouble() * (1.0 - p) + c2.getUGreen().toDouble() * p).toInt(),
                    (c1.getUBlue().toDouble() * (1.0 - p) + c2.getUBlue().toDouble() * p).toInt(),
                    (c1.getUAlpha().toDouble() * (1.0 - p) + c2.getUAlpha().toDouble() * p).toInt()
                )
            }
            Mode.Bands -> c1
        }
    }

    fun getEntries(): Set<Entry> {
        return entries
    }

    override val lowestFraction: Double
        protected get() = if (!entries.isEmpty()) {
            entries.first().fraction
        } else {
            super.lowestFraction
        }
    override val highestFraction: Double
        protected get() = if (!entries.isEmpty()) {
            entries.last().fraction
        } else {
            super.highestFraction
        }

    class Entry(val fraction: Double, color: MkColor) : Comparable<Entry> {
        private val color: MkColor
        fun getColor(): MkColor {
            return color
        }

        override operator fun compareTo(other: Entry): Int {
            return fraction.compareTo(other.fraction)
        }

        override fun equals(other: Any?): Boolean {
            if (this === other) return true
            if (other == null || this::class != other::class) return false

            other as Entry

            if (fraction != other.fraction) return false

            return true
        }

        override fun hashCode(): Int {
            return fraction.hashCode()
        }

        init {
            this.color = color
        }
    }

    init {
        updateFixedColorGradient()
    }
}